import React, { useEffect, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from 'components/ui/table';
import { DataTableToolbar, TableSearch, ColumnsToggle, TableCount, DataTablePagination } from 'components/ui/data-table';
import Avatar from 'components/ui/avatar';
import { usePeople } from 'modules/analytics';
import type { PeopleSortBy, PersonRow } from 'modules/analytics';
import type { AnalyticsFilters } from '../useAnalyticsFilters';
import { LoadingBlock, ErrorBlock, EmptyBlock, scoreColorClass } from './shared';

const PER_PAGE = 25;

const Sparkline: React.FC<{ values: number[] }> = ({ values }) => {
  if (values.length < 2) return <span className="text-xs text-muted-foreground/50">—</span>;
  const w = 64;
  const h = 20;
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const pts = values
    .map((v, i) => `${(i / (values.length - 1)) * w},${h - ((v - min) / span) * h}`)
    .join(' ');
  // Green when the latest score is at or above the first, red when it slipped.
  const rising = values[values.length - 1] >= values[0];
  return (
    <svg width={w} height={h} className="overflow-visible" aria-hidden>
      <polyline
        points={pts}
        fill="none"
        stroke={rising ? 'hsl(var(--success))' : 'hsl(var(--destructive))'}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

interface SortHeadProps {
  column: PeopleSortBy;
  label: string;
  filters: AnalyticsFilters;
  className?: string;
}

const SortHead: React.FC<SortHeadProps> = ({ column, label, filters, className }) => {
  const active = filters.sortBy === column;
  const toggle = () => filters.setSort(column, active && filters.sortDir === 'desc' ? 'asc' : 'desc');
  return (
    <TableHead className={className}>
      <button type="button" onClick={toggle} className="inline-flex items-center gap-1 transition-colors hover:text-foreground">
        {label}
        {active && (filters.sortDir === 'desc' ? <ArrowDown size={11} /> : <ArrowUp size={11} />)}
      </button>
    </TableHead>
  );
};

const PeopleTab: React.FC<{ orgId?: string; filters: AnalyticsFilters }> = ({ orgId, filters }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = usePeople({ orgId, params: filters.peopleParams });

  const [cols, setCols] = useState({
    attempts: true,
    avgScore: true,
    bestScore: true,
    passRate: true,
    avgTime: true,
    lastActive: true,
    trend: true,
  });

  // Debounced search → URL.
  const [term, setTerm] = useState(filters.q);
  useEffect(() => {
    const id = setTimeout(() => {
      if (term !== filters.q) filters.setQ(term);
    }, 350);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [term]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PER_PAGE)) : 1;

  return (
    <div className="space-y-6">
      <DataTableToolbar>
        <TableSearch
          value={term}
          onChange={setTerm}
          placeholder={t('analytics_people_search')}
          aria-label={t('analytics_people_search')}
        />
        <ColumnsToggle
          columns={[
            { id: 'attempts', label: t('attempts'), visible: cols.attempts },
            { id: 'avgScore', label: t('avg_score'), visible: cols.avgScore },
            { id: 'bestScore', label: t('analytics_people_best'), visible: cols.bestScore },
            { id: 'passRate', label: t('pass_rate'), visible: cols.passRate },
            { id: 'avgTime', label: t('analytics_people_avg_time'), visible: cols.avgTime },
            { id: 'lastActive', label: t('analytics_people_last_active'), visible: cols.lastActive },
            { id: 'trend', label: t('analytics_people_trend'), visible: cols.trend },
          ]}
          onChange={(id, visible) => setCols((c) => ({ ...c, [id]: visible }))}
        />
        {data && (
          <TableCount>
            {data.total} {t('analytics_people_candidates')}
          </TableCount>
        )}
      </DataTableToolbar>

      {isError ? (
        <ErrorBlock message={error instanceof Error ? error.message : undefined} />
      ) : isLoading ? (
        <LoadingBlock />
      ) : !data || data.rows.length === 0 ? (
        <EmptyBlock message={t('analytics_people_empty')} />
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table className="min-w-[760px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('analytics_people_candidate')}</TableHead>
                  {cols.attempts && <SortHead column="attempts" label={t('attempts')} filters={filters} />}
                  {cols.avgScore && <SortHead column="avgScore" label={t('avg_score')} filters={filters} />}
                  {cols.bestScore && <SortHead column="bestScore" label={t('analytics_people_best')} filters={filters} />}
                  {cols.passRate && <SortHead column="passRate" label={t('pass_rate')} filters={filters} />}
                  {cols.avgTime && <TableHead>{t('analytics_people_avg_time')}</TableHead>}
                  {cols.lastActive && (
                    <SortHead column="lastActiveAt" label={t('analytics_people_last_active')} filters={filters} />
                  )}
                  {cols.trend && <TableHead>{t('analytics_people_trend')}</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row: PersonRow) => (
                  <TableRow key={row.candidateId}>
                    <TableCell>
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={row.name} src={row.avatarUrl} size="sm" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">{row.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{row.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    {cols.attempts && <TableCell className="tabular-nums">{row.attempts}</TableCell>}
                    {cols.avgScore && (
                      <TableCell className={`tabular-nums font-semibold ${scoreColorClass(row.avgScore)}`}>{row.avgScore}%</TableCell>
                    )}
                    {cols.bestScore && <TableCell className="tabular-nums">{row.bestScore}%</TableCell>}
                    {cols.passRate && <TableCell className="tabular-nums">{row.passRate}%</TableCell>}
                    {cols.avgTime && (
                      <TableCell className="tabular-nums text-muted-foreground">
                        {row.avgDuration == null ? '—' : `${row.avgDuration}m`}
                      </TableCell>
                    )}
                    {cols.lastActive && (
                      <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                        {row.lastActiveAt ? new Date(row.lastActiveAt).toLocaleDateString() : '—'}
                      </TableCell>
                    )}
                    {cols.trend && (
                      <TableCell>
                        <Sparkline values={row.recentScores} />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {data.total > 0 && (
            <DataTablePagination
              page={filters.page}
              pageCount={totalPages}
              pageSize={PER_PAGE}
              total={data.total}
              onPageChange={filters.setPage}
              onPageSizeChange={() => {}}
              pageSizeOptions={[PER_PAGE]}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PeopleTab;
